import { useCallback, useState } from "react";
import { useIntl } from "react-intl";

import usePermissions from "hooks/usePermissions";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import EditOrganisationModal from "components/molecules/TopNav/shipper/NavIcons/EditOrganisationAfterDelivery/EditOrganisationModal";
import { canEditOrganisationAfterDelivery } from "components/molecules/TopNav/shipper/NavIcons/EditOrganisationAfterDelivery/canEditOrganisationAfterDelivery";
import EditTab from "components/molecules/TopNav/shipper/NavIcons/EditTab";
import { ShipmentTypes } from "constants/index";
import { FEATURE_FLAGS } from "constants/featureFlags";
import useFeatureFlag from "hooks/useFeatureFlag";

interface ShipperActionIconButtonsProps {
  isQuickRequest: boolean;
  shipmentId: string;
  tabTitle: string;
  isLoading: boolean;
  isConsolidated?: boolean;
}

export default function ShipperActionIconButtons({
  isQuickRequest,
  shipmentId,
  tabTitle,
  isLoading,
  isConsolidated,
}: Readonly<ShipperActionIconButtonsProps>) {
  const intl = useIntl();
  const [isOrganisationModalOpen, setIsOrganisationModalOpen] = useState(false);
  const openOrganisationModal = useCallback(() => setIsOrganisationModalOpen(true), []);
  const closeOrganisationModal = useCallback(() => setIsOrganisationModalOpen(false), []);
  const trackingTabTitle = intl.formatMessage({ id: "general.tracking", defaultMessage: "Tracking" });
  const isEditDeliveredOrganisationEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_10988_EDIT_DELIVERED_SHIPMENT_ORGANIZATION);

  const { data: shipmentData }: any = useGetShipmentBasic(shipmentId, {
    enabled: !isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });
  const { data: quickDetailsData }: any = useGetQuickRequestDetails(shipmentId, {
    enabled: isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });
  const { data: shipmentDetailsData = {} } = useGetShipmentDetails(shipmentId, true, {
    enabled: !!shipmentId && tabTitle === trackingTabTitle,
    refetchOnMount: false,
  });

  const { shipment } = shipmentData || {};
  const { permissions } = usePermissions();
  const { menu_items, user_type, permissions: permissionFlags } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";
  const editOtherShipments = Boolean(permissionFlags?.tracking?.edit_other_shipments);

  const quotationsAvailable = shipmentData?.shipment?.quotes_received > 0;

  const isConsolidatedRequest = isQuickRequest
    ? quickDetailsData?.related_request?.related_request_type === ShipmentTypes.CONSOLIDATED
    : isConsolidated;
  const shipmentStatus = shipment?.shipment_status;
  const status = isQuickRequest
    ? quickDetailsData?.organization?.quick_request_status
    : shipmentData?.organization?.request_status;

  const isOffPlatform = shipment?.is_off_platform_shipment || false;
  const isShipment = Boolean(shipmentStatus);
  const isQuoteRequestBooked = Boolean(shipmentDetailsData?.schedule?.booking_date);

  // Permissions
  const canEditCancel = isQuickRequest || (status !== "Delivered" && !isQuickRequest);
  const canEdit =
    !isOffPlatform &&
    status !== "Canceled" &&
    status !== "Auto canceled" &&
    canEditCancel &&
    (isQuickRequest ? menu_items?.create_quick_requests : menu_items?.create_quote_requests) &&
    (!isQuickRequest ||
      (status !== "Processing" && !quickDetailsData?.related_request?.related_request_id) ||
      isConsolidatedRequest) &&
    isShipper;
  const canEditOrganisation = canEditOrganisationAfterDelivery({
    isFeatureEnabled: isEditDeliveredOrganisationEnabled,
    isShipper,
    isGuest: false,
    isOffPlatform,
    isQuickRequest,
    requestStatus: status,
    editOtherShipments,
  });

  if (isLoading) return null;

  return (
    <>
      {canEdit && (
        <EditTab
          isQuickRequest={isQuickRequest}
          isConsolidated={isConsolidatedRequest}
          shipmentId={shipmentId}
          isShipment={isShipment}
          quotationsAvailable={quotationsAvailable}
          isQuoteRequestBooked={isQuoteRequestBooked}
        />
      )}
      {canEditOrganisation && (
        <EditTab
          isQuickRequest={isQuickRequest}
          isConsolidated={isConsolidatedRequest}
          shipmentId={shipmentId}
          isShipment={isShipment}
          quotationsAvailable={quotationsAvailable}
          isQuoteRequestBooked={isQuoteRequestBooked}
          onEditClick={openOrganisationModal}
        />
      )}
      <EditOrganisationModal
        isOpen={isOrganisationModalOpen}
        onClose={closeOrganisationModal}
        shipmentId={shipmentId}
        paymentLocation={shipmentData?.organization?.payment_location_reference}
      />
    </>
  );
}

ShipperActionIconButtons.defaultProps = {
  isConsolidated: false,
};
